// File: src/App.js
import { useEffect, useState, useRef } from 'react';
import logo from './invertedA.png';
import './App.css';
import './firebase.js';

// Expecting these exports from ./firebase.js
import { auth, rtdb } from './firebase.js';
import {
  GoogleAuthProvider,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';

import { ref as dbRef, update as dbUpdate, set as dbSet } from 'firebase/database';

function App() {
  const [loginState, setLoginState] = useState('idle'); // 'idle' | 'phone-collect' | 'phone-verify' | 'logged-in'
  const [errorState, setErrorState] = useState('');
  const [user, setUser] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [loadingProfileWrite, setLoadingProfileWrite] = useState(false);

  // Recaptcha container ref (invisible)
  const recaptchaVerifierRef = useRef(null);

  // Track auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        setLoginState('logged-in');
        setErrorState('');
        try {
          await writeProfileToRtdb(u);
        } catch (err) {
          console.warn('Failed to write profile at login:', err);
        }
      } else {
        setUser(null);
        setLoginState('idle');
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper: write or update profile under /profiles/{uid}
  async function writeProfileToRtdb(u) {
    if (!u || !u.uid) return;
    setLoadingProfileWrite(true);
    try {
      const profileRef = dbRef(rtdb, `profiles/${u.uid}`);
      const payload = {
        uid: u.uid,
        displayName: u.displayName || null,
        email: u.email || null,
        phoneNumber: u.phoneNumber || null,
        photoURL: u.photoURL || null,
        lastLogin: Date.now(),
        providerData: u.providerData ? JSON.stringify(u.providerData.map(p => ({ providerId: p.providerId, uid: p.uid, displayName: p.displayName || null }))) : null,
      };
      // Use update to avoid stomping other nodes
      await dbUpdate(profileRef, payload);
    } finally {
      setLoadingProfileWrite(false);
    }
  }

  // Google Sign-in
  async function handleGoogleSignIn() {
    setErrorState('');
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      // onAuthStateChanged handles profile write
    } catch (err) {
      console.error('Google sign-in error', err);
      setErrorState(err.message || 'Google sign-in failed');
    }
  }

  // Initialize Recaptcha (invisible)
  function ensureRecaptcha() {
    if (recaptchaVerifierRef.current) return recaptchaVerifierRef.current;
    // Recaptcha container id: 'recaptcha-container' (in markup)
    // Invisible Recaptcha
    const verifier = new RecaptchaVerifier(
      'recaptcha-container',
      {
        size: 'invisible',
        callback: (response) => {
          // recaptcha solved - allow sign-in flow to continue
          console.debug('Recaptcha solved');
        },
      },
      auth
    );
    // store instance globally
    recaptchaVerifierRef.current = verifier;
    return verifier;
  }

  // Begin phone sign-in: collect phone number then send code
  async function handleSendPhoneCode() {
    setErrorState('');
    if (!phoneNumber || !/^\+\d{7,15}$/.test(phoneNumber)) {
      setErrorState('Enter phone number in E.164 format, e.g. +919812345678');
      return;
    }
    setSendingCode(true);
    try {
      const verifier = ensureRecaptcha();
      // signInWithPhoneNumber returns a ConfirmationResult
      const result = await signInWithPhoneNumber(auth, phoneNumber, verifier);
      setConfirmResult(result);
      setLoginState('phone-verify');
      setErrorState('');
    } catch (err) {
      console.error('send phone code error', err);
      // Reset recaptcha in case of repeated attempts
      try {
        recaptchaVerifierRef.current && recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      } catch (_) {}
      setErrorState(err.message || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  }

  // Verify code received via SMS
  async function handleVerifyPhoneCode() {
    setErrorState('');
    if (!confirmResult) {
      setErrorState('No verification flow in progress. Start again.');
      return;
    }
    if (!verificationCode || verificationCode.trim().length < 4) {
      setErrorState('Enter the verification code you received by SMS.');
      return;
    }
    try {
      const credential = await confirmResult.confirm(verificationCode.trim());
      // credential.user will be signed in; onAuthStateChanged will handle profile write
      setVerificationCode('');
      setConfirmResult(null);
      setLoginState('logged-in');
    } catch (err) {
      console.error('confirm code error', err);
      setErrorState(err.message || 'Verification failed. Check code and try again.');
    }
  }

  // Sign out
  async function handleSignOut() {
    try {
      await signOut(auth);
      setUser(null);
      setLoginState('idle');
      setErrorState('');
    } catch (err) {
      console.error('sign-out error', err);
      setErrorState('Failed to sign out');
    }
  }

  // Small UI helpers
  function shortName(u) {
    if (!u) return '';
    return u.displayName || u.email || u.phoneNumber || u.uid.slice(0, 8);
  }

  // Render
  return (
    <div className="App" style={{ minHeight: '100vh', background: '#f6f7fb', padding: 20 }}>
      <header className="App-header" style={{ maxWidth: 920, margin: '0 auto', textAlign: 'center' }}>
        <img src={logo} style={{ marginTop: -40, height: 84 }} className="App-logo" alt="logo" />
        <p style={{ fontWeight: 700, fontSize: 36, color: '#0b1220', margin: '8px 0' }}>Ayana</p>
        <p style={{ fontWeight: 300, color: '#344054', marginTop: -8 }}>The silent space of Innernet!!</p>

        {!user && (
          <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={() => { setLoginState('login'); setErrorState(''); }}
              style={{ background: '#0f172a', color: '#fff', border: 'none', fontSize: 16, cursor: 'pointer', padding: '12px 18px', borderRadius: 10 }}
            >
              Play
            </button>

            <button
              onClick={handleGoogleSignIn}
              style={{ background: '#fff', color: '#0b1220', border: '1px solid #e6e9ef', fontSize: 16, cursor: 'pointer', padding: '12px 18px', borderRadius: 10 }}
            >
              Sign in with Google
            </button>

            <button
              onClick={() => { setLoginState('phone-collect'); setErrorState(''); }}
              style={{ background: '#fff', color: '#0b1220', border: '1px solid #e6e9ef', fontSize: 16, cursor: 'pointer', padding: '12px 18px', borderRadius: 10 }}
            >
              Sign in with Phone
            </button>
          </div>
        )}

        {user && (
          <div style={{ marginTop: 18, display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: '#fff', padding: 12, borderRadius: 12, boxShadow: '0 6px 20px rgba(15,23,42,0.06)' }}>
              <img src={user.photoURL || '/favicon.ico'} alt="avatar" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover' }} />
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 700, color: '#0b1220' }}>{shortName(user)}</div>
                <div style={{ fontSize: 13, color: '#475569' }}>{user.email || user.phoneNumber || ''}</div>
              </div>
              <div style={{ marginLeft: 12 }}>
                <button onClick={handleSignOut} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 10, cursor: 'pointer' }}>Sign out</button>
              </div>
            </div>
          </div>
        )}

        {/* Phone sign-in flow */}
        {loginState === 'phone-collect' && !user && (
          <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center' }}>
            <div style={{ background: '#fff', padding: 16, borderRadius: 12, boxShadow: '0 6px 20px rgba(15,23,42,0.04)', minWidth: 300 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#111827', marginBottom: 6 }}>Phone (E.164)</label>
              <input
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+919812345678"
                style={{ width: '100%', padding: 10, fontSize: 15, borderRadius: 8, border: '1px solid #e6e9ef' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={handleSendPhoneCode} disabled={sendingCode} style={{ flex: 1, background: '#0b1220', color: '#fff', border: 'none', padding: '10px', borderRadius: 8, cursor: 'pointer' }}>
                  {sendingCode ? 'Sending…' : 'Send code'}
                </button>
                <button onClick={() => setLoginState('idle')} style={{ background: '#fff', border: '1px solid #e6e9ef', padding: '10px', borderRadius: 8, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                You will receive an SMS to verify your phone.
              </div>
            </div>
          </div>
        )}

        {loginState === 'phone-verify' && !user && (
          <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center' }}>
            <div style={{ background: '#fff', padding: 16, borderRadius: 12, boxShadow: '0 6px 20px rgba(15,23,42,0.04)', minWidth: 300 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#111827', marginBottom: 6 }}>Enter verification code</label>
              <input
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="123456"
                style={{ width: '100%', padding: 10, fontSize: 15, borderRadius: 8, border: '1px solid #e6e9ef' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={handleVerifyPhoneCode} style={{ flex: 1, background: '#0b1220', color: '#fff', border: 'none', padding: '10px', borderRadius: 8, cursor: 'pointer' }}>
                  Verify
                </button>
                <button onClick={() => { setLoginState('phone-collect'); setVerificationCode(''); }} style={{ background: '#fff', border: '1px solid #e6e9ef', padding: '10px', borderRadius: 8, cursor: 'pointer' }}>
                  Back
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                Didn't get the code? Go back and resend.
              </div>
            </div>
          </div>
        )}

        {/* Recaptcha container required by Firebase phone auth */}
        <div id="recaptcha-container" style={{ marginTop: 8 }} />

        {/* Error */}
        {errorState && <div style={{ marginTop: 16, color: '#b91c1c', fontWeight: 600 }}>{errorState}</div>}

        {/* Profile write indicator */}
        {loadingProfileWrite && <div style={{ marginTop: 12, color: '#374151' }}>Updating profile…</div>}
      </header>
    </div>
  );
}

export default App;
